import { calculateTooltipCoordinates } from '../functions/tooltips';
import { useCallback } from 'react';
import { useTheme } from '@mui/material/styles';

/** @hook
 * To provide common event handlers for graphs in the desktop view.
 *
 * Common handlers include the ability to bind a tooltip and hover over effects
 * to a chart.
 * @param tooltipState {Object}
 * @param setTooltipState {function(Object)}
 * @param clearTooltip { function() }
 * @param getActiveDotStyle {function}
 * @param getInactiveDotStyle {function}
 * @param selectedDot {Object}
 * @param setSelectedDot {function}
 * @returns {{onClickDot: (function(*, *, *): [{mutation: (function(*): {}), eventKey: string, target: string},{mutation: (function(*): {size: *, style: *&{strokeWidth: *, stroke: *}}), target: string}]), onMouseOverDot: ((function(*, *, *): ([{mutation: function(*): {size: *, style: *&{strokeWidth: *, stroke: *}}, target: string}]|undefined))|*), onMouseOutDot: ((function(): ([{mutation: function(): {}, target: string}]|undefined))|*)}}
 */
export function useDesktopChartEventHandlers(
    { tooltipState, setTooltipState, clearTooltip },
    { getActiveDotStyle, getInactiveDotStyle },
    selectedDot,
    setSelectedDot
) {
    const theme = useTheme();

    const onMouseOverDot = (event, data, graphRef) => {
        if (!selectedDot) {
            const coordinates = calculateTooltipCoordinates(
                data.datum,
                graphRef,
                theme
            );
            setTooltipState({
                hovered: true,
                selected: false,
                posX: coordinates.xPos,
                posY: coordinates.yPos,
                selectedDot: data.datum
            });
            return [
                {
                    target: 'data',
                    mutation: (props) => getActiveDotStyle(props, theme)
                }
            ];
        }
    };

    const onMouseOutDot = useCallback(() => {
        if (!selectedDot) {
            clearTooltip();
            return [
                {
                    target: 'data',
                    mutation: () => ({})
                }
            ];
        }
    }, [clearTooltip, tooltipState]);

    const onClickDot = useCallback(
        (event, data, graphRef) => {
            event.stopPropagation();

            const coordinates = calculateTooltipCoordinates(
                data.datum,
                graphRef,
                theme
            );

            setTooltipState({
                hovered: false,
                selected: true,
                posX: coordinates.xPos,
                posY: coordinates.yPos,
                selectedDot: data.datum
            });
            setSelectedDot({ row: data.datum, isClicked: true });
            return [
                {
                    target: 'data',
                    eventKey: 'all',
                    mutation: (props) => getInactiveDotStyle(props, theme, true)
                }
            ];
        },
        [setTooltipState]
    );

    return {
        onMouseOverDot,
        onMouseOutDot,
        onClickDot
    };
}
